/**
 * Returns client (arc Inc 6) — the Returns Admin card feeds. Mirrors the fee
 * View-History client.
 *
 * Backend router: app/routers/returns.py, prefix /returns (Admin-only).
 *   - GET /returns/agreement/{id}?period_from=YYYY-MM&period_to=YYYY-MM
 *   - GET /returns/month?period=YYYY-MM
 *
 * Returns are float ratios (0.0123 = +1.23%). Period figures are geometrically
 * compounded (cumulative) + annualized (null when the window < 12 months).
 */
import { api } from './client';
import type { ISODate, UUID } from './types';

/** TWR fidelity rung (procedure §8). Account grain is only ever 'exact'|'gap';
 * agreement grain adds 'manual'|'saa'. null on historical (uploaded) months. */
export type Fidelity = 'exact' | 'manual' | 'saa' | 'gap';

export interface AccountReturn {
  account_id: UUID;
  label: string;
  return_ratio: number | null;
  fidelity: Fidelity | null;
  fidelity_reason: string | null;
}

export interface AgreementReturnMonth {
  period_month: ISODate;
  source: 'historical' | 'computed';
  return_ratio: number | null;
  fidelity: Fidelity | null;
  fidelity_reason: string | null;
  fidelity_gap_kind: string | null;   // machine tag of the gap CAUSE, e.g. 'material_flow' (§5.4)
  flags: string[];
  accounts: AccountReturn[];
}

export interface ReturnSummary {
  cumulative: number | null;
  annualized: number | null;
  n_months: number;
  n_with_return: number;
}

export interface AgreementReturns {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  months: AgreementReturnMonth[];
  summary: ReturnSummary;
}

export interface FirmwideReturnRow {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  /** P44: the agreement's SAA and Primary Named RM(s). */
  saa?: string | null;
  rm_label?: string | null;
  month: AgreementReturnMonth;
}

/** §10 regulatory "show-your-work" audit for one (agreement, month) — Inc 4e. `inputs` and
 * `trace` are loosely typed (the trace step shapes vary by `step`); the backend is authoritative. */
export interface ReturnAuditStep {
  step: 'opening' | 'flow' | 'eop' | 'result' | 'gap';
  [k: string]: unknown;
}
export interface ReturnAudit {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string | null;
  period_month: ISODate;
  ratio: number | null;
  rung: Fidelity;
  gap_reason: string | null;
  inputs: {
    contributing_accounts: {
      account_id: UUID; label: string;
      eom_cents: number | null; prior_eom_cents: number | null;
      flows: { date: string; amount_cents: number; kind: string }[];
    }[];
    sum_bop_cents: number | null;
    sum_eop_cents: number | null;
    agreement_flows: { date: string; amount_cents: number; kind: string }[];
    saa_profile: { id: UUID; code: string } | null;
    manual_mtds: { flow_date: string; mtd_return: number }[];
  };
  trace: ReturnAuditStep[];
}

export const returnsApi = {
  /** One agreement's blended monthly returns over [from, to] ('YYYY-MM') + roll-up. */
  agreement: (id: UUID, from: string, to: string): Promise<AgreementReturns> =>
    api.get<AgreementReturns>(
      `/returns/agreement/${id}?period_from=${encodeURIComponent(from)}&period_to=${encodeURIComponent(to)}`,
    ),

  /** Every agreement's return for one month ('YYYY-MM'). */
  month: (period: string): Promise<FirmwideReturnRow[]> =>
    api.get<FirmwideReturnRow[]>(`/returns/month?period=${encodeURIComponent(period)}`),

  /** §10 audit — the full inputs + step-by-step calculation for one (agreement, month 'YYYY-MM'). */
  audit: (id: UUID, period: string): Promise<ReturnAudit> =>
    api.get<ReturnAudit>(`/returns/agreement/${id}/audit?period=${encodeURIComponent(period)}`),
};
